/**
 * Web Worker for offloading session recorder flush operations:
 * - Base64 encoding of packed rrweb events
 * - JSON serialization of the full payload
 * - HTTP POST to the collector
 *
 * This keeps the main thread free from the expensive serialization work
 * that was causing 5s+ freezes with large DOM snapshots.
 */

export interface FlushWorkerRequest {
	type: 'flush';
	id: number;
	collectorUrl: string;
	headers: Record<string, string>;
	sessionId: string;
	userId?: string;
	events: Array<{ event: unknown; capturedAt: number }>;
	devtools?: unknown;
	/** Pre-gzipped + base64 devtools (preferred over inline devtools). */
	devtoolsGz?: string;
}

export interface FlushWorkerResponse {
	type: 'flush-result';
	id: number;
	ok: boolean;
	eventsCount: number;
	status?: number;
	error?: string;
}

/**
 * Returns the worker script as a string for inline Blob construction.
 * This avoids needing webpack worker-loader or separate entry points.
 */
export function getFlushWorkerScript(): string {
	return `
'use strict';

function stringToBase64(str) {
	var bytes = new Uint8Array(str.length);
	for (var i = 0; i < str.length; i++) {
		bytes[i] = str.charCodeAt(i);
	}
	var CHUNK = 0x8000;
	var binary = '';
	for (var i = 0; i < bytes.length; i += CHUNK) {
		binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
	}
	return btoa(binary);
}

var EVENT_TYPE_NAMES = {
	0: 'DomContentLoaded',
	1: 'Load',
	2: 'FullSnapshot',
	3: 'IncrementalSnapshot',
	4: 'Meta',
	5: 'Custom',
	6: 'Plugin'
};

function encodePackedEvent(event) {
	if (typeof event === 'string') {
		return stringToBase64(event);
	}
	return JSON.stringify(event);
}

function mapEventsToPayload(events) {
	return events.map(function(item) {
		var event = item.event;
		var capturedAt = item.capturedAt;
		var isPacked = typeof event === 'string';
		return {
			eventType: isPacked ? 'Packed' : (EVENT_TYPE_NAMES[event.type] || 'Unknown'),
			eventData: encodePackedEvent(event),
			timestampMs: isPacked ? capturedAt : event.timestamp
		};
	});
}

self.onmessage = function(e) {
	var msg = e.data;
	if (msg.type !== 'flush') return;

	var payload = {
		sessionId: msg.sessionId,
		events: mapEventsToPayload(msg.events)
	};
	if (msg.userId) payload.userId = msg.userId;
	if (msg.devtoolsGz) {
		payload.devtoolsGz = msg.devtoolsGz;
	} else {
		payload.devtools = msg.devtools;
	}

	var body = JSON.stringify(payload);

	fetch(msg.collectorUrl + '/api/events', {
		method: 'POST',
		headers: msg.headers,
		body: body
	}).then(function(response) {
		self.postMessage({
			type: 'flush-result',
			id: msg.id,
			ok: response.ok,
			eventsCount: msg.events.length,
			status: response.status,
			error: response.ok ? undefined : 'HTTP ' + response.status
		});
	}).catch(function(err) {
		self.postMessage({
			type: 'flush-result',
			id: msg.id,
			ok: false,
			eventsCount: msg.events.length,
			error: err.message || String(err)
		});
	});
};
`;
}

/**
 * Creates an inline Web Worker from the flush script.
 * Falls back to null if Workers are not supported.
 */
export function createFlushWorker(): Worker | null {
	try {
		const blob = new Blob([getFlushWorkerScript()], { type: 'application/javascript' });
		const url = URL.createObjectURL(blob);
		const worker = new Worker(url);
		URL.revokeObjectURL(url);
		return worker;
	} catch {
		return null;
	}
}
